import { Pricing } from '../../types/cms.types';
import { PricingService, PricingFilter, PricingListResponse } from '../../service/supabase/PricingService';
import IBloc from '../../base/IBloc';

export interface PricingState {
    pricings: Pricing[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    loading: boolean;
    error: string | null;
    filters: PricingFilter;
    provinces: string[];
}

export interface PricingViewState {
    pricings: Pricing[];
    originalPricings: Pricing[]; // Dữ liệu gốc để filter local
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    loading: boolean;
    error: string | null;
    filters: PricingFilter;
    localFilter: {
        province: string;
    };
    hasData: boolean;
    provinces: string[];
}

export class BlocManagementPricing extends IBloc {
    private pricingService: PricingService;
    
    /**
     * State chính của Pricing Management
     */
    info: PricingViewState = {
        pricings: [],
        originalPricings: [], // Dữ liệu gốc
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        loading: false,
        error: null,
        filters: {},
        localFilter: {
            province: ''
        },
        hasData: false,
        provinces: []
    };

    constructor() {
        super();
        this.pricingService = PricingService.getInstance();
    }

    /**
     * Khởi tạo dữ liệu
     */
    initData() {
        console.log('BlocManagementPricing: initData called');
        this.setStream("info", this.info);
        this.loadProvinces();
        this.loadPricings();
    }

    /**
     * Load danh sách tỉnh thành
     */
    async loadProvinces(): Promise<void> {
        try {
            const provinces = await this.pricingService.getProvinces();
            this.info.provinces = provinces;
            this.setStream("info", this.info);
        } catch (error) {
            console.error('Error loading provinces:', error);
        }
    }

    /**
     * Load all pricings without pagination
     */
    async loadPricings(filters?: PricingFilter): Promise<void> {
        try {
            console.log('BlocManagementPricing: loadPricings called', { filters });
            this.info.error = null;
            this.info.loading = true;
            this.setStream("info", this.info);

            const filtersToApply = filters ?? this.info.filters;

            // Update filters if provided
            if (filters) {
                this.info.filters = filtersToApply;
            }

            // Load all data without pagination (set very high limit)
            const response: PricingListResponse = await this.pricingService.getPricings(
                1,
                1000, // Load all provinces at once
                filtersToApply
            );
            
            console.log('BlocManagementPricing: API response', response);

            // Update state
            this.info.pricings = response.data;
            this.info.originalPricings = response.data; // Lưu dữ liệu gốc
            this.info.pagination = {
                total: response.total,
                page: 1,
                limit: response.total, // Show all data
                totalPages: 1
            };
            this.info.hasData = response.data.length > 0;
            this.info.loading = false;

            // Apply local filter if exists
            if (this.info.localFilter.province) {
                this.applyLocalFilter();
            }

            this.setStream("info", this.info);
            console.log('BlocManagementPricing: Updated state', this.info);
        } catch (error) {
            console.error('BlocManagementPricing: Error loading pricings', error);
            this.info.loading = false;
            this.info.error = error instanceof Error ? error.message : 'Có lỗi xảy ra khi tải dữ liệu';
            this.setStream("info", this.info);
        }
    }

    /**
     * Refresh data - reload với filters hiện tại
     */
    refreshData(): void {
        console.log('BlocManagementPricing: refreshData called');
        this.loadPricings(this.info.filters);
    }

    /**
     * Chuyển trang (không cần thiết vì load all data)
     */
    goToPage(page: number): void {
        console.log('BlocManagementPricing: goToPage called - not needed for all data load', page);
        // Không cần thiết vì đã load tất cả dữ liệu
    }

    /**
     * Thay đổi số lượng items per page (không cần thiết)
     */
    changeLimit(limit: number): void {
        console.log('BlocManagementPricing: changeLimit called - not needed for all data load', limit);
        // Không cần thiết vì đã load tất cả dữ liệu
    }

    /**
     * Apply filters (clear local filter và load từ server)
     */
    applyFilters(filters: PricingFilter): void {
        console.log('BlocManagementPricing: applyFilters called', filters);
        
        // Clear local filter khi apply server filter
        this.info.localFilter.province = '';
        
        this.loadPricings(filters);
    }
  
    /**
     * Apply local filter khi chọn tỉnh thành - không cần pagination
     */
    private applyLocalFilter(): void {
        if (!this.info.localFilter.province) {
            // Nếu không có filter province, hiển thị tất cả dữ liệu gốc
            this.info.pricings = [...this.info.originalPricings];
        } else {
            // Filter theo tỉnh thành
            this.info.pricings = this.info.originalPricings.filter(pricing => 
                pricing.province.toLowerCase().includes(this.info.localFilter.province.toLowerCase())
            );
        }
        
        // Update total count
        this.info.pagination.total = this.info.pricings.length;
        this.info.hasData = this.info.pricings.length > 0;
    }

    /**
     * Search local khi chọn tỉnh thành
     */
    searchLocalProvince(province: string): void {
        console.log('BlocManagementPricing: searchLocalProvince called', province);
        
        this.info.localFilter.province = province;
        
        this.applyLocalFilter();
        this.setStream("info", this.info);
    }

    /**
     * Clear local filter
     */
    clearLocalFilter(): void {
        console.log('BlocManagementPricing: clearLocalFilter called');
        
        this.info.localFilter.province = '';
        
        this.applyLocalFilter();
        this.setStream("info", this.info);
    }

    /**
     * Clear filters (clear cả server filter và local filter)
     */
    clearFilters(): void {
        console.log('BlocManagementPricing: clearFilters called');
        
        // Clear local filter
        this.info.localFilter.province = '';
        
        this.loadPricings({}); // Load với filter rỗng
    }

    /**
     * Tạo pricing mới
     */
    async createPricing(pricing: Omit<Pricing, 'id' | 'created_at' | 'updated_at'>): Promise<Pricing> {
        try {
            console.log('BlocManagementPricing: createPricing called', pricing);
            
            const newPricing = await this.pricingService.createPricing(pricing);
            console.log('BlocManagementPricing: Pricing created successfully', newPricing);
            
            // Refresh data after creation
            this.refreshData();
            
            return newPricing;
        } catch (error) {
            console.error('BlocManagementPricing: Error creating pricing', error);
            throw error;
        }
    }

    /**
     * Cập nhật pricing
     */
    async updatePricing(id: string, pricing: Partial<Omit<Pricing, 'id' | 'created_at' | 'updated_at'>>): Promise<Pricing> {
        try {
            console.log('BlocManagementPricing: updatePricing called', id, pricing);
            
            const updatedPricing = await this.pricingService.updatePricing(id, pricing);
            console.log('BlocManagementPricing: Pricing updated successfully', updatedPricing);
            
            // Refresh data after update
            this.refreshData();
            
            return updatedPricing;
        } catch (error) {
            console.error('BlocManagementPricing: Error updating pricing', error);
            throw error;
        }
    }

    /**
     * Xóa pricing
     */
    async deletePricing(id: string): Promise<void> {
        try {
            console.log('BlocManagementPricing: deletePricing called', id);
            
            await this.pricingService.deletePricing(id);
            console.log('BlocManagementPricing: Pricing deleted successfully');
            
            // Refresh data after deletion
            this.refreshData();
        } catch (error) {
            console.error('BlocManagementPricing: Error deleting pricing', error);
            throw error;
        }
    }

    /**
     * Lấy chi tiết pricing theo ID
     */
    async getPricingById(id: string): Promise<Pricing | null> {
        try {
            console.log('BlocManagementPricing: getPricingById called', id);
            
            const pricing = await this.pricingService.getPricingById(id);
            console.log('BlocManagementPricing: Pricing detail loaded', pricing);
            
            return pricing;
        } catch (error) {
            console.error('BlocManagementPricing: Error loading pricing detail', error);
            throw error;
        }
    }

    /**
     * Sao chép bảng giá từ một tỉnh sang nhiều tỉnh khác
     */
    async copyPricingToProvinces(
        sourcePricingId: string, 
        targetProvinces: string[]
    ): Promise<{ 
        success: number; 
        failed: number; 
        results: Array<{ province: string; success: boolean; error?: string }> 
    }> {
        try {
            console.log('BlocManagementPricing: copyPricingToProvinces called', {
                sourcePricingId,
                targetProvinces
            });
            
            const result = await this.pricingService.copyPricingToProvinces(
                sourcePricingId, 
                targetProvinces
            );
            
            console.log('BlocManagementPricing: Copy pricing completed', result);
            
            // Refresh data after copy operation
            this.refreshData();
            
            return result;
        } catch (error) {
            console.error('BlocManagementPricing: Error copying pricing', error);
            throw error;
        }
    }

    /**
     * Dispose bloc
     */
    dispose(): void {
        console.log('BlocManagementPricing: Disposing bloc');
        super.dispose();
    }
}
